/** 退款列表（只读）。发起退款走网关 API。 */

import { useState } from 'react';
import { usePagedList } from '../lib/hooks.ts';
import { Card, CopyId, Field, Money, Select, StatusBadge, TextInput, Badge } from '../components/ui.tsx';
import { DataTable, Pager, type Column } from '../components/table.tsx';
import { formatDateTime, statusLabel } from '../lib/format.ts';
import type { Refund } from '../lib/types.ts';

const REFUND_STATUSES = ['pending', 'processing', 'succeeded', 'failed', 'cancelled'];

export function RefundsPage() {
  const [status, setStatus] = useState('');
  const [paymentId, setPaymentId] = useState('');

  const list = usePagedList<Refund>('/v1/refunds', {
    status: status === '' ? null : status,
    payment_id: paymentId === '' ? null : paymentId,
  });

  const columns: readonly Column<Refund>[] = [
    { key: 'id', header: '退款号', render: (row) => <CopyId id={row.id} /> },
    { key: 'payment', header: '原交易', render: (row) => (
      <div className="stack tight">
        <CopyId id={row.payment_id} />
        {row.order_no !== undefined && <span className="cell-muted mono" style={{ fontSize: 11 }}>{row.order_no}</span>}
      </div>
    ) },
    { key: 'amount', header: '金额', numeric: true, render: (row) => (
      <Money value={row.amount} currency={row.currency} />
    ) },
    { key: 'status', header: '状态', render: (row) => <StatusBadge status={row.status} /> },
    { key: 'reason', header: '原因', render: (row) => (
      <span className="cell-muted">{row.reason ?? '—'}</span>
    ) },
    { key: 'error', header: '失败信息', render: (row) => (
      row.error_code === null ? <span className="cell-muted">—</span> : (
        <div className="row tight">
          <Badge tone={row.error_class === 'hard_decline' ? 'bad' : 'warn'}>{row.error_class ?? '未分类'}</Badge>
          <span className="mono">{row.error_code}</span>
        </div>
      )
    ) },
    { key: 'connector', header: '通道退款号', render: (row) => (
      row.connector_refund_id === null
        ? <span className="cell-muted">—</span>
        : <CopyId id={row.connector_refund_id} full />
    ) },
    { key: 'created', header: '时间', render: (row) => (
      <span className="cell-muted mono">{formatDateTime(row.created_at)}</span>
    ) },
  ];

  return (
    <>
      <h1>退款</h1>

      <div className="filters">
        <Field label="状态">
          <Select
            value={status}
            onChange={setStatus}
            placeholder="全部"
            options={REFUND_STATUSES.map((value) => ({ value, label: statusLabel(value) }))}
          />
        </Field>
        <Field label="原交易号">
          <TextInput value={paymentId} onChange={setPaymentId} placeholder="pay_…" />
        </Field>
      </div>

      <Card flush>
        <DataTable
          columns={columns}
          rows={list.items}
          loading={list.loading}
          error={list.error}
          rowKey={(row) => row.id}
          empty="没有符合条件的退款"
        />
        <Pager
          page={list.page}
          hasNext={list.hasNext}
          hasPrev={list.hasPrev}
          onNext={list.next}
          onPrev={list.prev}
          loading={list.loading}
          count={list.items.length}
        />
      </Card>
    </>
  );
}
